<template>
  <div class="download-container">
    <div class="download-header">
      <div class="header-content">
        <button @click.stop="goBack" class="back-button">
          <span class="back-icon">←</span>
          <span>返回</span>
        </button>
        <div class="header-title">
          <div class="title-icon">⬇️</div>
          <h1>素材下载</h1>
        </div>
      </div>
    </div>

    <div class="download-content">
      <div class="current-shop-section" v-if="advertiserName">
        <div class="shop-info-container">
          <div class="shop-icon">🏪</div>
          <div class="shop-details">
            <h3 class="shop-name">当前授权店铺</h3>
            <p class="shop-advertiser">{{ advertiserName }}</p>
          </div>
        </div>
      </div>

      <div class="download-layout">
        <div class="download-sidebar">
          <div class="form-card">
            <div class="form-title-row">
              <h2 class="form-title">素材查询条件</h2>
            </div>
            <div class="form-grid form-grid-query">
              <div class="input-group">
                <label for="download-advertiser-id">千川户ID</label>
                <el-select
                  id="download-advertiser-id"
                  v-model="advertiserId"
                  filterable
                  placeholder="请选择千川户"
                  class="input-group-select"
                  :disabled="advertiserLoading"
                  :popper-append-to-body="true"
                  @change="onAdvertiserChange"
                >
                  <el-option
                    v-for="item in advertiserOptions"
                    :key="item.advertiserId"
                    :label="item.name"
                    :value="item.advertiserId"
                  />
                </el-select>
              </div>

              <div class="input-group">
                <label for="download-ad-id">计划ID</label>
                <el-select
                  id="download-ad-id"
                  v-model="adId"
                  filterable
                  placeholder="请选择计划ID"
                  class="input-group-select"
                  :disabled="jobLoading || !advertiserId"
                  :popper-append-to-body="true"
                >
                  <el-option
                    v-for="item in jobOptions"
                    :key="item.id"
                    :label="item.name"
                    :value="item.id"
                  />
                </el-select>
              </div>

              <div class="query-action-group">
                <label class="query-action-label">查询操作</label>
                <button class="query-button" @click="queryMaterials" :disabled="queryLoading">
                  <span v-if="queryLoading" class="button-spinner"></span>
                  <span>{{ queryLoading ? '查询中...' : '查询' }}</span>
                </button>
              </div>
            </div>
          </div>

          <div class="form-card plugin-install-card">
            <div class="plugin-install-head">
              <h3 class="plugin-install-title">插件安装</h3>
              <div class="plugin-install-desc">下载 `material-download-helper.zip` 到用户目录，自动解压并打开文件夹</div>
            </div>
            <button class="plugin-install-button" @click="confirmInstallPluginZip" :disabled="installingPlugin">
              <span v-if="installingPlugin" class="button-spinner"></span>
              <span>{{ installingPlugin ? '安装处理中...' : '下载并安装插件压缩包' }}</span>
            </button>
          </div>

          <div v-if="statusMessage" class="status-message" :class="{ success: statusType === 'success', error: statusType === 'error' }">
            {{ statusMessage }}
          </div>

          <div v-if="batchDownloading" class="download-progress-panel">
            <div class="download-progress-head">
              <span>下载进度</span>
              <span>{{ downloadProgressText }}</span>
            </div>
            <div class="download-progress-bar">
              <div class="download-progress-bar-inner" :style="{ width: downloadProgressPercent + '%' }"></div>
            </div>
          </div>
        </div>
        <div class="download-main">
          <div class="result-card">
            <div class="result-header">
              <div class="result-header-left">
                <h3 class="result-title">素材列表</h3>
                <span class="result-count">共 {{ materials.length }} 条</span>
                <span class="result-count">筛选后 {{ filteredMaterials.length }} 条</span>
                <span class="result-count">已选 {{ selectedMaterialKeys.length }} 条</span>
              </div>
              <div class="result-header-actions">
                <label class="search-label">
                  <span>标题搜索</span>
                  <input
                    v-model.trim="titleKeyword"
                    type="text"
                    class="search-input"
                    placeholder="输入标题关键字"
                    :disabled="batchDownloading"
                    @input="handleKeywordChange"
                  />
                </label>
                <label class="page-size-label">
                  <span>每页</span>
                  <select v-model.number="pageSize" class="page-size-select" @change="handlePageSizeChange">
                    <option v-for="size in pageSizeOptions" :key="size" :value="size">{{ size }}</option>
                  </select>
                  <span>条</span>
                </label>
                <button
                  class="page-select-button"
                  @click="toggleSelectAllMaterials"
                  :disabled="materials.length === 0 || batchDownloading"
                >
                  {{ isAllMaterialsSelected ? '取消全部' : '全选全部' }}
                </button>
                <button
                  class="page-select-button"
                  @click="toggleSelectCurrentPage"
                  :disabled="paginatedMaterials.length === 0 || batchDownloading"
                >
                  {{ isCurrentPageFullySelected ? '取消本页全选' : '本页全选' }}
                </button>
                <button
                  class="page-select-button page-clear-button"
                  @click="clearAllSelections"
                  :disabled="selectedMaterialKeys.length === 0 || batchDownloading"
                >
                  清空选择
                </button>
              </div>
            </div>

            <div v-if="!queried" class="result-empty">
              请选择千川户ID和计划ID后点击查询。
            </div>
            <div v-else-if="materials.length === 0" class="result-empty">
              当前计划下暂无可展示素材。
            </div>
            <div v-else-if="filteredMaterials.length === 0" class="result-empty">
              当前筛选条件下没有匹配的素材。
            </div>
            <div v-else class="material-list">
              <div v-for="(item, index) in paginatedMaterials" :key="getMaterialKey(item, currentPageStartIndex + index)" class="material-item">
                <div class="material-item-left">
                  <label class="material-check-cell">
                    <input
                      type="checkbox"
                      class="material-checkbox"
                      :checked="isMaterialSelected(item, currentPageStartIndex + index)"
                      @change="toggleMaterialSelection(item, currentPageStartIndex + index)"
                    />
                    <span class="material-checkbox-ui"></span>
                  </label>
                  <div class="material-index">{{ currentPageStartIndex + index + 1 }}</div>
                  <div class="material-main material-main-left">
                    <div class="material-title">{{ item.title || '未命名素材' }}</div>
                    <div class="material-subtitle">请使用插件下载或插件批量下载功能保存素材</div>
                  </div>
                </div>
                <div class="material-preview-cell">
                  <video
                    class="material-video"
                    :src="item.url"
                    controls
                    preload="metadata"
                    playsinline
                  >
                    当前浏览器不支持视频预览
                  </video>
                </div>
              </div>
            </div>

            <div v-if="filteredMaterials.length > 0" class="pagination-bar">
              <button class="pagination-button" :disabled="currentPage <= 1 || batchDownloading" @click="changePage(currentPage - 1)">
                上一页
              </button>
              <span class="pagination-info">第 {{ currentPage }} / {{ totalPages }} 页</span>
              <button class="pagination-button" :disabled="currentPage >= totalPages || batchDownloading" @click="changePage(currentPage + 1)">
                下一页
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import API_CONFIG from '@/api/config'
import { createAdaptiveViewportMixin } from '@/mixins/adaptiveViewport'

export default {
  name: 'MaterialDownload',
  mixins: [createAdaptiveViewportMixin({ designWidth: 1440, designHeight: 940, minScale: 0.54 })],
  data() {
    return {
      accessToken: '',
      advertiserName: '',
      username: '',
      advertiserOptions: [],
      advertiserLoading: false,
      advertiserId: '',
      adId: '',
      jobOptions: [],
      jobLoading: false,
      queryLoading: false,
      batchDownloading: false,
      batchDownloadIndex: 0,
      batchDownloadTotal: 0,
      downloadingMaterialKey: '',
      queried: false,
      materials: [],
      selectedMaterialKeys: [],
      titleKeyword: '',
      currentPage: 1,
      pageSize: 100,
      pageSizeOptions: [20, 50, 100],
      installingPlugin: false,
      statusMessage: '',
      statusType: ''
    }
  },
  computed: {
    filteredMaterials() {
      const keyword = String(this.titleKeyword || '').trim().toLowerCase()
      if (!keyword) {
        return this.materials
      }
      return this.materials.filter(item => String(item.title || '').toLowerCase().includes(keyword))
    },
    totalPages() {
      const total = Math.ceil(this.filteredMaterials.length / this.pageSize)
      return total > 0 ? total : 1
    },
    currentPageStartIndex() {
      return (this.currentPage - 1) * this.pageSize
    },
    paginatedMaterials() {
      return this.filteredMaterials.slice(this.currentPageStartIndex, this.currentPageStartIndex + this.pageSize)
    },
    isCurrentPageFullySelected() {
      return this.paginatedMaterials.length > 0 && this.paginatedMaterials.every((item, index) => {
        return this.isMaterialSelected(item, this.currentPageStartIndex + index)
      })
    },
    isAllMaterialsSelected() {
      return this.filteredMaterials.length > 0 && this.filteredMaterials.every((item, index) => {
        return this.selectedMaterialKeys.includes(this.getMaterialKey(item, index))
      })
    },
    downloadProgressText() {
      if (!this.batchDownloadTotal) {
        return ''
      }
      return `${this.batchDownloadIndex}/${this.batchDownloadTotal}`
    },
    downloadProgressPercent() {
      if (!this.batchDownloadTotal) {
        return 0
      }
      return Math.min(100, Math.round((this.batchDownloadIndex / this.batchDownloadTotal) * 100))
    }
  },
  created() {
    const query = this.$route.query
    this.accessToken = query.accessToken || localStorage.getItem('accessToken') || ''
    this.advertiserName = query.advertiserName || localStorage.getItem('advertiserName') || ''
    this.username = query.username || localStorage.getItem('username') || ''
    this.updatePageTitle()
    if (this.accessToken) {
      this.fetchAdvertiserOptions()
    }
  },
  methods: {
    updatePageTitle() {
      const name = String(this.advertiserName || '').trim()
      document.title = name ? `${name} - 素材下载` : '素材下载'
    },
    goBack() {
      this.$router.push('/home')
    },
    async fetchAdvertiserOptions() {
      this.advertiserLoading = true
      try {
        const response = await axios.get(API_CONFIG.getFullUrl('/qianchuan/info/api/getShopAdvertiserPublicSimpleInfo'), {
          params: {
            accessToken: this.accessToken
          },
          headers: {
            username: this.username
          }
        })
        const list = response.data && response.data.code === 200 ? (response.data.data || []) : []
        this.advertiserOptions = list.map(item => ({
          name: item.id || '-',
          advertiserId: item.advertiser_id
        })).filter(item => item.advertiserId)
      } catch (error) {
        this.advertiserOptions = []
      } finally {
        this.advertiserLoading = false
      }
    },
    async fetchJobOptions(advertiserId) {
      if (!advertiserId) {
        return []
      }
      const response = await axios.get(API_CONFIG.getFullUrl('/qianchuan/info/api/getShopAdvertiserJobInfo'), {
        params: {
          accessToken: this.accessToken,
          advertiserId
        },
        headers: {
          username: this.username
        }
      })
      const list = response.data && response.data.code === 200 ? (response.data.data || []) : []
      return list.map(item => ({
        id: item.id,
        name: item.name || String(item.id || '-')
      })).filter(item => item.id)
    },
    async onAdvertiserChange() {
      this.adId = ''
      this.jobOptions = []
      this.materials = []
      this.selectedMaterialKeys = []
      this.titleKeyword = ''
      this.currentPage = 1
      this.queried = false
      if (!this.advertiserId) {
        return
      }
      this.jobLoading = true
      try {
        this.jobOptions = await this.fetchJobOptions(this.advertiserId)
      } catch (error) {
        this.jobOptions = []
      } finally {
        this.jobLoading = false
      }
    },
    async queryMaterials() {
      if (!this.accessToken) {
        this.statusType = 'error'
        this.statusMessage = '缺少access_token，请返回授权页重新进入'
        return
      }
      if (!this.advertiserId || !this.adId) {
        this.statusType = 'error'
        this.statusMessage = '请先选择千川户ID和计划ID'
        return
      }

      this.queryLoading = true
      this.statusMessage = ''
      this.statusType = ''
      this.materials = []
      this.selectedMaterialKeys = []
      this.titleKeyword = ''
      this.currentPage = 1
      this.queried = true

      try {
        const response = await axios.post(API_CONFIG.materialDownloadList(), {
          advertiser_id: Number(this.advertiserId),
          ad_id: Number(this.adId)
        }, {
          headers: {
            'Content-Type': 'application/json',
            username: this.username
          }
        })

        if (response.data && response.data.code === 200) {
          this.materials = Array.isArray(response.data.data) ? response.data.data : []
          this.statusType = 'success'
          this.statusMessage = `查询完成，共获取到 ${this.materials.length} 条素材`
        } else {
          this.statusType = 'error'
          this.statusMessage = response.data?.msg || '素材查询失败'
        }
      } catch (error) {
        this.statusType = 'error'
        this.statusMessage = error.response?.data?.msg || '素材查询请求失败'
      } finally {
        this.queryLoading = false
      }
    },
    getMaterialKey(item, fallbackIndex) {
      if (item && item.materialId !== null && item.materialId !== undefined && item.materialId !== '') {
        return String(item.materialId)
      }
      const title = String((item && item.title) || 'material').trim()
      const url = String((item && item.url) || '').trim()
      if (title || url) {
        return `${title}|${url}`
      }
      return `material-${fallbackIndex}`
    },
    isMaterialSelected(item, fallbackIndex) {
      return this.selectedMaterialKeys.includes(this.getMaterialKey(item, fallbackIndex))
    },
    toggleMaterialSelection(item, fallbackIndex) {
      const key = this.getMaterialKey(item, fallbackIndex)
      if (this.selectedMaterialKeys.includes(key)) {
        this.selectedMaterialKeys = this.selectedMaterialKeys.filter(itemKey => itemKey !== key)
        return
      }
      this.selectedMaterialKeys = [...this.selectedMaterialKeys, key]
    },
    toggleSelectCurrentPage() {
      const currentKeys = this.paginatedMaterials.map((item, index) => this.getMaterialKey(item, this.currentPageStartIndex + index))
      if (this.isCurrentPageFullySelected) {
        this.selectedMaterialKeys = this.selectedMaterialKeys.filter(key => !currentKeys.includes(key))
        return
      }
      this.selectedMaterialKeys = Array.from(new Set([...this.selectedMaterialKeys, ...currentKeys]))
    },
    toggleSelectAllMaterials() {
      if (this.isAllMaterialsSelected) {
        const filteredKeys = this.filteredMaterials.map((item, index) => this.getMaterialKey(item, index))
        this.selectedMaterialKeys = this.selectedMaterialKeys.filter(key => !filteredKeys.includes(key))
        return
      }
      const filteredKeys = this.filteredMaterials.map((item, index) => this.getMaterialKey(item, index))
      this.selectedMaterialKeys = Array.from(new Set([...this.selectedMaterialKeys, ...filteredKeys]))
    },
    clearAllSelections() {
      this.selectedMaterialKeys = []
    },
    handleKeywordChange() {
      this.currentPage = 1
    },
    handlePageSizeChange() {
      if (this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages
      } else {
        this.currentPage = 1
      }
    },
    changePage(page) {
      if (page < 1 || page > this.totalPages) {
        return
      }
      this.currentPage = page
    },
    sanitizeFileName(name) {
      const baseName = String(name || 'video')
        .replace(/[\\/:*?"<>|]/g, '_')
        .replace(/\s+/g, ' ')
        .trim()
      return baseName || 'video'
    },
    normalizeMediaUrl(rawUrl) {
      return String(rawUrl || '')
        .replace(/[`'"]/g, '')
        .trim()
    },
    getLocalPluginInstallApiUrl() {
      const origin = String(window.location.origin || '')
      if (/^https?:\/\/(127\.0\.0\.1|localhost)(:\d+)?$/i.test(origin)) {
        return `${origin}/api/plugin/install-helper`
      }
      return 'http://127.0.0.1:3000/api/plugin/install-helper'
    },
    async showConfirmDialog(contentHtml) {
      if (typeof this.$confirm === 'function') {
        return this.$confirm(contentHtml, '安装插件压缩包', {
          dangerouslyUseHTMLString: true,
          confirmButtonText: '确认下载并解压',
          cancelButtonText: '取消',
          type: 'info'
        })
      }

      const plainText = [
        '确认后将执行以下操作：',
        '1. 把 material-download-helper.zip 下载到当前用户目录',
        '2. 自动解压插件文件',
        '3. 自动打开解压后的文件夹',
        '',
        '后续加载方式：',
        '1. 打开 Edge/Chrome 扩展管理页',
        '2. 开启开发者模式',
        '3. 点击“加载已解压的扩展程序”',
        '4. 选择打开目录中的 material-download-helper 文件夹'
      ].join('\n')

      if (window.confirm(plainText)) {
        return Promise.resolve()
      }
      return Promise.reject(new Error('cancel'))
    },
    async showAlertDialog(contentHtml, plainTextTitle) {
      if (typeof this.$alert === 'function') {
        return this.$alert(contentHtml, plainTextTitle, {
          dangerouslyUseHTMLString: true,
          confirmButtonText: '知道了'
        })
      }
      window.alert(plainTextTitle)
      return Promise.resolve()
    },
    async confirmInstallPluginZip() {
      const content = [
        '<div style="line-height:1.8;">',
        '<div>确认后将执行以下操作：</div>',
        '<div>1. 把 <b>material-download-helper.zip</b> 下载到当前用户目录</div>',
        '<div>2. 自动解压插件文件</div>',
        '<div>3. 自动打开解压后的文件夹</div>',
        '<div style="margin-top:8px;">后续加载方式：</div>',
        '<div>1. 打开 Edge/Chrome 扩展管理页</div>',
        '<div>2. 开启开发者模式</div>',
        '<div>3. 点击“加载已解压的扩展程序”</div>',
        '<div>4. 选择打开目录中的 <b>material-download-helper</b> 文件夹</div>',
        '</div>'
      ].join('')

      try {
        await this.showConfirmDialog(content)
      } catch (error) {
        return
      }

      this.installingPlugin = true
      try {
        const response = await axios.post(this.getLocalPluginInstallApiUrl())
        const result = response.data || {}
        if (!result.success) {
          throw new Error(result.message || '插件压缩包处理失败')
        }

        const extractDir = result.data && result.data.extractDir ? result.data.extractDir : ''
        const instructions = result.data && Array.isArray(result.data.instructions) ? result.data.instructions : []
        this.statusType = 'success'
        this.statusMessage = '插件压缩包已下载并解压，目录已自动打开'

        await this.showAlertDialog(
          [
            '<div style="line-height:1.8;">',
            '<div>插件压缩包已处理完成，目录已自动打开。</div>',
            extractDir ? `<div style="margin-top:8px;">解压目录：<br/>${extractDir}</div>` : '',
            instructions.length ? `<div style="margin-top:8px;">${instructions.join('<br/>')}</div>` : '',
            '</div>'
          ].join(''),
          ['安装完成', extractDir, ...(instructions || [])].filter(Boolean).join('\n')
        )
      } catch (error) {
        this.statusType = 'error'
        this.statusMessage = error.response?.data?.message || error.message || '插件压缩包处理失败'
      } finally {
        this.installingPlugin = false
      }
    },
    async triggerVideoDownload(url, fileName) {
      const directUrl = this.normalizeMediaUrl(url)
      if (!directUrl) {
        throw new Error('当前素材缺少可用的视频链接')
      }
      const link = document.createElement('a')
      link.href = directUrl
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    async downloadSingleMaterial(item, fallbackIndex) {
      const materialKey = this.getMaterialKey(item, fallbackIndex)
      this.downloadingMaterialKey = materialKey
      try {
        await this.triggerVideoDownload(item.url, this.sanitizeFileName(item.title))
        this.statusType = 'success'
        this.statusMessage = `已打开下载链接：${item.title || '未命名素材'}`
      } catch (error) {
        this.statusType = 'error'
        this.statusMessage = error.message || '当前视频下载失败，请重试'
      } finally {
        this.downloadingMaterialKey = ''
      }
    },
    async downloadSelectedMaterials() {
      const selectedItems = this.materials.filter((item, index) => this.selectedMaterialKeys.includes(this.getMaterialKey(item, index)))
      if (selectedItems.length === 0) {
        this.statusType = 'error'
        this.statusMessage = '请先勾选要下载的视频素材'
        return
      }

      this.batchDownloading = true
      this.batchDownloadIndex = 0
      this.batchDownloadTotal = selectedItems.length
      this.statusType = 'success'
      this.statusMessage = `开始批量下载，共 ${selectedItems.length} 个视频`

      try {
        for (let i = 0; i < selectedItems.length; i++) {
          const item = selectedItems[i]
          this.batchDownloadIndex = i + 1
          await this.triggerVideoDownload(item.url, this.sanitizeFileName(item.title))
          await new Promise(resolve => setTimeout(resolve, 260))
        }
        this.statusType = 'success'
        this.statusMessage = `已依次打开 ${selectedItems.length} 个下载链接`
      } catch (error) {
        this.statusType = 'error'
        this.statusMessage = error.message || '批量下载过程中发生异常，请重试'
      } finally {
        this.batchDownloading = false
        this.batchDownloadIndex = 0
        this.batchDownloadTotal = 0
      }
    }
  }
}
</script>

<style scoped>
.download-container {
  min-height: 100vh;
  height: 100vh;
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.download-header {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  padding: 14px 0;
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  align-items: center;
  gap: 20px;
}

.back-button {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 25px;
  padding: 10px 20px;
  color: white;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.back-button:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateX(-5px);
}

.header-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.title-icon {
  font-size: 24px;
}

.header-title h1 {
  color: white;
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.download-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 12px 14px 14px;
  overflow: hidden;
  min-height: 0;
  box-sizing: border-box;
}

.download-layout {
  flex: 1;
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  gap: 16px;
  align-items: start;
  min-height: 0;
}

.download-sidebar,
.download-main {
  min-width: 0;
}

.download-sidebar {
  align-self: start;
}

.download-main {
  min-height: 0;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  padding-right: 4px;
  padding-bottom: 14px;
  box-sizing: border-box;
}

.current-shop-section {
  margin-bottom: 12px;
}

.shop-info-container {
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.shop-icon {
  font-size: 24px;
}

.shop-details h3,
.shop-details p {
  margin: 0;
}

.shop-name {
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  font-weight: 500;
}

.shop-advertiser {
  color: white;
  font-size: 18px;
  font-weight: 600;
  margin-top: 4px;
}

.form-card,
.result-card {
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
}

.form-card {
  padding: 14px;
  margin-bottom: 10px;
}

.plugin-install-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.plugin-install-head {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.plugin-install-title {
  margin: 0;
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
}

.plugin-install-desc {
  color: rgba(255, 255, 255, 0.72);
  font-size: 12px;
  line-height: 1.6;
}

.plugin-install-button {
  height: 40px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(45deg, #8854d0, #a55eea);
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.plugin-install-button:disabled {
  opacity: 0.72;
  cursor: not-allowed;
}

.plugin-install-button:not(:disabled):hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 18px rgba(165, 94, 234, 0.35);
}

.result-card {
  padding: 14px;
  overflow: visible;
}

.form-title-row,
.result-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
}

.form-title,
.result-title {
  margin: 0;
  color: white;
  font-size: 20px;
}

.result-count {
  color: rgba(255, 255, 255, 0.75);
  font-size: 12px;
}

.result-header-left,
.result-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 12px;
}

.download-sidebar .form-grid {
  grid-template-columns: 1fr;
}

.form-grid-query {
  align-items: end;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.input-group label,
.query-action-label {
  color: rgba(255, 255, 255, 0.85);
  font-size: 14px;
}

.input-group-select {
  width: 100%;
}

.query-action-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.query-button {
  height: 44px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(45deg, #20bf6b, #0fb9b1);
  color: white;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.search-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
}

.search-input {
  width: 180px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(255, 255, 255, 0.12);
  color: white;
  padding: 0 10px;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.55);
}

.search-input:focus {
  border-color: rgba(143, 214, 255, 0.7);
  box-shadow: 0 0 0 2px rgba(143, 214, 255, 0.14);
}

.page-size-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
}

.page-size-select {
  height: 34px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  background: rgba(255, 255, 255, 0.12);
  color: white;
  padding: 0 10px;
  outline: none;
}

.page-size-select option {
  color: #1f2a44;
}

.page-select-button,
.download-selected-button {
  height: 36px;
  border: none;
  border-radius: 10px;
  padding: 0 14px;
  color: white;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.page-select-button {
  background: linear-gradient(45deg, #3867d6, #4b7bec);
}

.download-selected-button {
  background: linear-gradient(45deg, #fa8231, #fd9644);
}

.page-clear-button {
  background: linear-gradient(45deg, #6c5ce7, #8e7dff);
}

.page-select-button:disabled,
.download-selected-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.query-button:disabled {
  opacity: 0.8;
  cursor: not-allowed;
}

.page-select-button:not(:disabled):hover,
.download-selected-button:not(:disabled):hover,
.query-button:not(:disabled):hover {
  transform: translateY(-2px);
}

.query-button:not(:disabled):hover {
  box-shadow: 0 8px 18px rgba(15, 185, 177, 0.35);
}

.page-select-button:not(:disabled):hover {
  box-shadow: 0 8px 18px rgba(75, 123, 236, 0.35);
}

.download-selected-button:not(:disabled):hover {
  box-shadow: 0 8px 18px rgba(253, 150, 68, 0.35);
}

.page-clear-button:not(:disabled):hover {
  box-shadow: 0 8px 18px rgba(142, 125, 255, 0.35);
}

::v-deep .input-group-select .el-input__inner {
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 10px;
  color: white;
  font-size: 14px;
  height: 44px;
  line-height: 44px;
}

::v-deep .input-group-select .el-input__suffix-inner i {
  color: rgba(255, 255, 255, 0.75);
}

::v-deep .input-group-select.is-disabled .el-input__inner {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.55);
}

::v-deep .el-select-dropdown__item {
  color: #1f2a44;
}

.button-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.status-message {
  margin-bottom: 10px;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 13px;
}

.download-progress-panel {
  margin-bottom: 10px;
  border-radius: 12px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.18);
}

.download-progress-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: rgba(255, 255, 255, 0.88);
  font-size: 13px;
  margin-bottom: 10px;
}

.download-progress-bar {
  width: 100%;
  height: 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  overflow: hidden;
}

.download-progress-bar-inner {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(45deg, #20bf6b, #0fb9b1);
  transition: width 0.25s ease;
}

.status-message.success {
  background: rgba(46, 213, 115, 0.2);
  border: 1px solid rgba(46, 213, 115, 0.4);
  color: #7dffb8;
}

.status-message.error {
  background: rgba(255, 107, 107, 0.2);
  border: 1px solid rgba(255, 107, 107, 0.4);
  color: #ff9f9f;
}

.result-empty {
  color: rgba(255, 255, 255, 0.75);
  font-size: 14px;
  padding: 18px 6px;
}

.material-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.material-item {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) 300px;
  gap: 12px;
  align-items: stretch;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.14);
}

.material-item-left {
  min-width: 0;
  display: grid;
  grid-template-columns: 24px 32px minmax(0, 1fr);
  gap: 10px;
  align-items: center;
}

.material-check-cell {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.material-checkbox {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.material-checkbox-ui {
  width: 18px;
  height: 18px;
  border-radius: 5px;
  border: 1px solid rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.08);
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.material-checkbox:checked + .material-checkbox-ui {
  background: #4b7bec;
  border-color: #6ea8ff;
}

.material-checkbox:checked + .material-checkbox-ui::after {
  content: '✓';
  color: #fff;
  font-size: 12px;
  font-weight: 700;
}

.material-index {
  min-width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(102, 126, 234, 0.24);
  color: #dce6ff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
}

.material-main {
  min-width: 0;
  flex: 1;
}

.material-main-left {
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.material-title {
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  word-break: break-all;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.material-subtitle {
  margin-top: 4px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  line-height: 1.4;
}

.material-preview-cell {
  width: 100%;
}

.material-video {
  width: 100%;
  height: 132px;
  display: block;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.35);
  object-fit: cover;
}

.single-download-button {
  width: 100%;
  margin-top: 8px;
  height: 34px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(45deg, #20bf6b, #26de81);
  color: white;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.single-download-button:disabled {
  opacity: 0.72;
  cursor: not-allowed;
}

.single-download-button:not(:disabled):hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 18px rgba(38, 222, 129, 0.3);
}

.pagination-bar {
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
}

.pagination-button {
  min-width: 90px;
  height: 36px;
  border: none;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.14);
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
}

.pagination-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.pagination-button:not(:disabled):hover {
  background: rgba(255, 255, 255, 0.22);
}

.pagination-info {
  color: rgba(255, 255, 255, 0.82);
  font-size: 13px;
}

@media (max-width: 1280px) {
  .download-layout {
    grid-template-columns: 300px minmax(0, 1fr);
  }

  .material-item {
    grid-template-columns: minmax(200px, 1fr) 260px;
  }

  .material-video {
    height: 118px;
  }
}

@media (max-width: 980px) {
  .download-layout {
    grid-template-columns: 1fr;
  }

  .result-header {
    align-items: flex-start;
  }

  .material-item {
    grid-template-columns: 1fr;
  }

  .material-preview-cell {
    grid-column: auto;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
